import * as React from 'react';
import { Field, FieldContent, FieldDescription, FieldError, FieldLabel } from '@bliks-io/ui';
import { SystemSelect } from './SystemSelect';

type SystemSelectFilter = React.ComponentProps<typeof SystemSelect>['filter'];

export interface FormFieldSystemSelectProps {
  /**
   * Selected value (string for single-select, string[] for multi-select)
   */
  value: string | string[] | undefined;

  /**
   * Change handler
   */
  onChange: (value: string | string[] | undefined) => void;

  /**
   * Enable multi-select mode
   */
  multiple?: boolean;

  /**
   * Field label
   */
  label?: string;

  /**
   * Field description/helper text
   */
  description?: string;

  /**
   * Error message
   */
  error?: string;

  /**
   * Mark field as required
   */
  required?: boolean;

  /**
   * Placeholder text
   */
  placeholder?: string;

  /**
   * Whether the select is disabled
   */
  disabled?: boolean;

  /**
   * Field ID
   */
  id?: string;

  /**
   * Field name
   */
  name?: string;

  /**
   * Field orientation
   */
  orientation?: 'vertical' | 'horizontal' | 'responsive';

  /**
   * Additional class for Field wrapper
   */
  fieldClassName?: string;

  /**
   * Additional class for SystemSelect
   */
  className?: string;

  /**
   * Allow clearing the selection
   */
  allowClear?: boolean;

  /**
   * Custom label for the clear/all option
   */
  clearLabel?: string;

  /**
   * Optional callback for custom clear behavior
   */
  onClear?: () => void;

  /**
   * Client-side filter function to apply before internal search filter
   */
  filter?: SystemSelectFilter;
}

/**
 * SystemSelect with Field wrapper (label, description, error)
 * Can be used standalone or wrapped by FormSystemSelect for RHF integration
 *
 * @example
 * ```tsx
 * const [systemId, setSystemId] = useState<string>();
 *
 * <FormFieldSystemSelect
 *   label="Select System"
 *   value={systemId}
 *   onChange={setSystemId}
 *   required
 *   error={errors.systemId?.message}
 * />
 * ```
 */
export const FormFieldSystemSelect = React.forwardRef<
  HTMLButtonElement,
  FormFieldSystemSelectProps
>(
  (
    {
      value,
      onChange,
      multiple = false,
      label,
      description,
      error,
      required = false,
      placeholder,
      disabled = false,
      id,
      name,
      orientation = 'vertical',
      fieldClassName,
      className,
      allowClear = false,
      clearLabel,
      onClear,
      filter
    },
    ref
  ) => {
    const fieldId = id || name;
    const hasError = !!error;

    return (
      <Field orientation={orientation} className={fieldClassName} data-invalid={hasError}>
        {label && (
          <FieldLabel htmlFor={fieldId}>
            {label}
            {required && <span className="text-destructive ml-1">*</span>}
          </FieldLabel>
        )}
        <FieldContent>
          {description && <FieldDescription>{description}</FieldDescription>}
          <SystemSelect
            ref={ref}
            id={fieldId}
            name={name}
            value={value}
            onChange={onChange}
            multiple={multiple}
            placeholder={placeholder}
            disabled={disabled}
            className={className}
            allowClear={allowClear}
            clearLabel={clearLabel}
            onClear={onClear}
            filter={filter}
          />
          {error && <FieldError>{error}</FieldError>}
        </FieldContent>
      </Field>
    );
  }
);

FormFieldSystemSelect.displayName = 'FormFieldSystemSelect';
